import { driver } from "driver.js";
import "driver.js/dist/driver.css";

export const TOUR_STORAGE_KEY = "virality_tour_completed";

export function isTourCompleted() {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function markTourCompleted() {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  } catch {
    // ignore
  }
}

export function resetTourCompleted() {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Base Driver.js configuration styled specifically for Virality
 */
function createDriverInstance(steps, extraConfig = {}) {
  let driverObj;
  driverObj = driver({
    animate: true,
    smoothScroll: true,
    overlayColor: "#181311",
    overlayOpacity: 0.65,
    stagePadding: 8,
    stageRadius: 14,
    popoverClass: "virality-driver-popover",
    showProgress: true,
    progressText: "Step {{current}} of {{total}}",
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Get Started 🚀",
    allowClose: true,
    allowKeyboardControl: true,
    skipMissingElement: true,
    steps,
    onCloseClick: (_el, _step, opts) => {
      markTourCompleted();
      if (opts?.driver) {
        opts.driver.destroy();
      } else if (driverObj) {
        driverObj.destroy();
      }
    },
    onDoneClick: (_el, _step, opts) => {
      markTourCompleted();
      if (opts?.driver) {
        opts.driver.destroy();
      } else if (driverObj) {
        driverObj.destroy();
      }
    },
    onDestroyed: () => {
      markTourCompleted();
      if (extraConfig.onDestroyed) extraConfig.onDestroyed();
    },
    ...extraConfig,
  });
  return driverObj;
}

/**
 * Complete Virality Website Tour
 * Uses simple everyday English with zero complicated jargon.
 */
export function startWebsiteTour(navigate) {
  markTourCompleted();
  const isDashboard = window.location.pathname === "/dashboard";

  // If not on Dashboard, navigate there first then start the tour
  if (!isDashboard && typeof navigate === "function") {
    navigate("/dashboard");
    setTimeout(() => {
      runDashboardTour();
    }, 450);
    return;
  }

  runDashboardTour();
}

function runDashboardTour() {
  const steps = [
    {
      popover: {
        title: "Welcome to Virality! 👋",
        description:
          "Here is a quick tour to help you get started. We will show you where to find video ideas, write scripts, and track your progress.",
        nextBtnText: "Start Tour →",
      },
    },
    {
      element: "#tour-sidebar-workspace",
      popover: {
        title: "Your Main Menu",
        description:
          "This is your menu. Use it to jump between finding ideas, writing scripts, and managing your videos.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-testid="nav-intelligence"]',
      popover: {
        title: "Trending Topics",
        description:
          "See what people are watching and talking about right now on YouTube and Instagram so you know what to make next.",
        side: "right",
        align: "center",
      },
    },
    {
      element: '[data-testid="nav-opportunities"]',
      popover: {
        title: "Best Video Ideas",
        description:
          "These are video topics picked for your niche, ranked from best to worst based on how well they should perform.",
        side: "right",
        align: "center",
      },
    },
    {
      element: '[data-testid="nav-ideas"]',
      popover: {
        title: "Ideas & Scripts",
        description:
          "Create catchy opening hooks that grab attention in the first 3 seconds, plus full video scripts you can record.",
        side: "right",
        align: "center",
      },
    },
    {
      element: "#tour-next-action",
      popover: {
        title: "What to Do Next",
        description:
          "A simple recommendation on the best action you should take next to keep your channel growing.",
        side: "bottom",
        align: "start",
      },
    },
    {
      element: "#tour-stats-cards",
      popover: {
        title: "Your Numbers",
        description:
          "See how many ideas you made, how many scripts are ready, and which accounts you connected.",
        side: "bottom",
        align: "start",
      },
    },
    {
      element: "#tour-creator-dna",
      popover: {
        title: "Your Creator Profile",
        description:
          "Fill in your style, topics, and audience. This makes sure every script our AI writes sounds like you.",
        side: "top",
        align: "start",
      },
    },
    {
      element: "#tour-activity-chart",
      popover: {
        title: "Your 30-Day Activity",
        description:
          "See how consistently you've created content over the last month to help you stay on track.",
        side: "top",
        align: "start",
      },
    },
    {
      element: "#tour-topbar-actions",
      popover: {
        title: "Your Account & Settings",
        description:
          "Manage your plan, change settings, or replay this tour anytime from your Settings page.",
        side: "bottom",
        align: "end",
      },
    },
    {
      popover: {
        title: "You're All Set! 🎉",
        description:
          "You are ready to create! Check out trending topics or make your first script now. You can replay this tour anytime from Settings.",
        doneBtnText: "Get Started 🚀",
        nextBtnText: "Get Started 🚀",
        onDoneClick: (_el, _step, opts) => {
          markTourCompleted();
          opts?.driver?.destroy();
        },
      },
    },
  ];

  const driverObj = createDriverInstance(steps);
  driverObj.drive();
}
